import React from 'react';

const SingularPopup = ({ closePopup, singularData }) => {
    const parseJsonIfNeeded = (data) => {
        if (typeof data === 'string') {
            try {
                return JSON.parse(data);
            } catch (e) {
                return data;
            }
        }
        return data;
    };

    const rootData = parseJsonIfNeeded(singularData);
    const flowData = rootData?.data || rootData?.result || rootData;

    // Check if we have application, asset, or documents fields in flowData
    const hasSingularityFlow = flowData && (flowData.application || flowData.asset || flowData.documents);

    const renderErrorTable = (errors, title = "Validation Errors") => {
        const parsedErrors = parseJsonIfNeeded(errors);
        if (!parsedErrors || typeof parsedErrors !== 'object') return null;

        const entries = Array.isArray(parsedErrors)
            ? parsedErrors.map((err, idx) => [`Error ${idx + 1}`, err])
            : Object.entries(parsedErrors);

        if (entries.length === 0) return null;

        return (
            <div style={{
                marginTop: '12px',
                border: '1px solid #fed7d7',
                borderRadius: '8px',
                overflow: 'hidden',
                backgroundColor: '#ffffff',
                boxShadow: '0 2px 4px rgba(220, 53, 69, 0.08)'
            }}>
                {/* Header bar */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    backgroundColor: '#fff5f5',
                    padding: '8px 12px',
                    borderBottom: '1px solid #fed7d7'
                }}>
                    <span style={{ fontWeight: '700', color: '#c53030', fontSize: '12px', display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '6px', fontSize: '13px' }}>⚠️</span> {title}
                    </span>
                    <span style={{
                        backgroundColor: '#e53e3e',
                        color: '#ffffff',
                        fontSize: '11px',
                        fontWeight: '700',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        letterSpacing: '0.3px'
                    }}>
                        {entries.length} {entries.length === 1 ? 'Error' : 'Errors'}
                    </span>
                </div>

                {/* Scrollable table container: scrollable both vertically and horizontally */}
                <div style={{
                    maxHeight: '190px',
                    overflowY: 'auto',
                    overflowX: 'auto',
                    WebkitOverflowScrolling: 'touch'
                }}>
                    <table style={{
                        width: '100%',
                        minWidth: '520px',
                        borderCollapse: 'collapse',
                        fontSize: '12px',
                        margin: 0
                    }}>
                        <thead>
                            <tr style={{
                                backgroundColor: '#fffaf0',
                                borderBottom: '2px solid #feebc8',
                                position: 'sticky',
                                top: 0,
                                zIndex: 2
                            }}>
                                <th style={{
                                    width: '38%',
                                    padding: '8px 12px',
                                    textAlign: 'left',
                                    fontWeight: '700',
                                    color: '#744210',
                                    textTransform: 'uppercase',
                                    fontSize: '10.5px',
                                    letterSpacing: '0.5px',
                                    borderRight: '1px solid #feebc8'
                                }}>
                                    Field / Parameter
                                </th>
                                <th style={{
                                    width: '62%',
                                    padding: '8px 12px',
                                    textAlign: 'left',
                                    fontWeight: '700',
                                    color: '#744210',
                                    textTransform: 'uppercase',
                                    fontSize: '10.5px',
                                    letterSpacing: '0.5px'
                                }}>
                                    Error Details
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map(([key, value], idx) => (
                                <tr
                                    key={idx}
                                    style={{
                                        borderBottom: idx === entries.length - 1 ? 'none' : '1px solid #f7fafc',
                                        backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fffdfd'
                                    }}
                                >
                                    <td style={{
                                        padding: '9px 12px',
                                        verticalAlign: 'top',
                                        borderRight: '1px solid #edf2f7',
                                        fontWeight: '600'
                                    }}>
                                        <code style={{
                                            display: 'inline-block',
                                            backgroundColor: '#f7fafc',
                                            border: '1px solid #e2e8f0',
                                            color: '#2d3748',
                                            padding: '3px 8px',
                                            borderRadius: '4px',
                                            fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                                            fontSize: '11px',
                                            wordBreak: 'break-all'
                                        }}>
                                            {key}
                                        </code>
                                    </td>
                                    <td style={{
                                        padding: '9px 12px',
                                        verticalAlign: 'top',
                                        color: '#e53e3e'
                                    }}>
                                        {Array.isArray(value) ? (
                                            value.length === 1 ? (
                                                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                                    <span style={{ marginRight: '6px', color: '#e53e3e', fontSize: '13px' }}>•</span>
                                                    <span style={{ fontWeight: '500', lineHeight: '1.4' }}>
                                                        {typeof value[0] === 'object' ? JSON.stringify(value[0]) : String(value[0])}
                                                    </span>
                                                </div>
                                            ) : (
                                                <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                                    {value.map((msg, mIdx) => (
                                                        <li key={mIdx} style={{ marginBottom: '4px', fontWeight: '500', lineHeight: '1.4' }}>
                                                            {typeof msg === 'object' ? JSON.stringify(msg) : String(msg)}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )
                                        ) : typeof value === 'object' && value !== null ? (
                                            <pre style={{
                                                margin: 0,
                                                fontSize: '11px',
                                                whiteSpace: 'pre-wrap',
                                                wordBreak: 'break-word',
                                                backgroundColor: '#fff5f5',
                                                padding: '6px',
                                                borderRadius: '4px',
                                                border: '1px solid #fed7d7',
                                                color: '#c53030'
                                            }}>
                                                {JSON.stringify(value, null, 2)}
                                            </pre>
                                        ) : (
                                            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                                                <span style={{ marginRight: '6px', color: '#e53e3e', fontSize: '13px' }}>•</span>
                                                <span style={{ fontWeight: '500', lineHeight: '1.4' }}>{String(value)}</span>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderSingularityFlow = () => {
        const appInfo = flowData?.application;
        const assetInfo = flowData?.asset;
        const docInfo = flowData?.documents;

        // Parse Application data
        const appInner = parseJsonIfNeeded(appInfo?.data) || appInfo;
        const appDetails = parseJsonIfNeeded(appInner?.data) || appInner;
        const appErrors = appDetails?.errors || appInner?.errors || appInfo?.errors || appInfo?.data?.errors;
        const parsedAppErrors = parseJsonIfNeeded(appErrors);
        const hasAppErrors = parsedAppErrors && typeof parsedAppErrors === 'object' && Object.keys(parsedAppErrors).length > 0;
        const appMessage = appDetails?.message || appInfo?.message || (hasAppErrors ? 'Errors' : 'N/A');
        const applicantId = appDetails?.applicant_id || appDetails?.data?.applicant_id || 'N/A';
        const appExists = appDetails?.exists ?? appDetails?.data?.exists;
        const applicantCif = appDetails?.applicant_cif || appDetails?.data?.applicant_cif || [];

        // Parse Asset data
        const assetInner = parseJsonIfNeeded(assetInfo?.data) || assetInfo;
        const assetDetails = parseJsonIfNeeded(assetInner?.data) || assetInner;
        const assetErrors = assetDetails?.errors || assetInner?.errors || assetInfo?.errors || assetInfo?.data?.errors;
        const parsedAssetErrors = parseJsonIfNeeded(assetErrors);
        const hasAssetErrors = parsedAssetErrors && typeof parsedAssetErrors === 'object' && Object.keys(parsedAssetErrors).length > 0;
        const assetMessage = assetDetails?.message || assetInfo?.message || (hasAppErrors && !assetInfo ? 'Not initiated (Application step failed)' : 'N/A');
        const assetId = assetDetails?.assets?.asset_id || assetDetails?.asset_id || 'N/A';

        // Parse Documents data
        const docInner = parseJsonIfNeeded(docInfo?.data) || docInfo;
        const docErrors = docInner?.errors || docInfo?.errors || docInfo?.data?.errors;
        const parsedDocErrors = parseJsonIfNeeded(docErrors);
        const hasDocErrors = parsedDocErrors && typeof parsedDocErrors === 'object' && Object.keys(parsedDocErrors).length > 0;
        const totalDocs = docInner?.totalDocuments ?? 0;
        const successCount = docInner?.successCount ?? 0;
        const failureCount = docInner?.failureCount ?? 0;
        const docResults = docInner?.results || [];
        const isDocFailed = hasDocErrors || failureCount > 0;

        return (
            <div style={{ maxHeight: '520px', overflowY: 'auto', textAlign: 'left', padding: '0 10px' }}>
                {/* Flow Overview Alert if failed */}
                {rootData?.message && (rootData?.status >= 400 || (typeof rootData.status === 'string' && rootData.status !== 'success') || hasAppErrors) && (
                    <div style={{
                        padding: '10px 14px',
                        marginBottom: '15px',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffeeba',
                        color: '#856404',
                        borderRadius: '6px',
                        fontSize: '13px'
                    }}>
                        <strong>Flow Notice:</strong> {rootData.message}
                    </div>
                )}

                {/* Application Section */}
                <div style={{
                    marginBottom: '15px',
                    padding: '12px',
                    background: hasAppErrors ? '#fff8f8' : '#f8f9fa',
                    borderRadius: '6px',
                    border: hasAppErrors ? '1px solid #f5c6cb' : '1px solid #e9ecef'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #dee2e6', paddingBottom: '6px', marginBottom: '8px' }}>
                        <h5 style={{ margin: 0, color: hasAppErrors ? '#721c24' : '#1D3557', fontWeight: '700' }}>
                            📋 Application Status
                        </h5>
                        {hasAppErrors ? (
                            <span style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                color: '#fff',
                                backgroundColor: '#dc3545'
                            }}>
                                FAILED
                            </span>
                        ) : null}
                    </div>
                    <div style={{ fontSize: '13px', lineHeight: '1.6', marginBottom: (applicantCif.length > 0 || hasAppErrors) ? '10px' : '0' }}>
                        <div>
                            <strong>Message:</strong>{' '}
                            <span style={{ color: hasAppErrors ? '#dc3545' : '#2b2d42', fontWeight: hasAppErrors ? '600' : 'normal' }}>
                                {appMessage}
                            </span>
                        </div>
                        {applicantId !== 'N/A' && <div><strong>Applicant ID:</strong> {applicantId}</div>}
                        {appExists !== undefined && <div><strong>CIF Exists:</strong> {appExists ? 'Yes' : 'No'}</div>}
                        {appDetails?.error_id && (
                            <div><strong>Error Code:</strong> <span style={{ color: '#dc3545' }}>{appDetails.error_id}</span></div>
                        )}
                    </div>

                    {/* Application Errors Table */}
                    {hasAppErrors && renderErrorTable(parsedAppErrors, "Application Validation Errors")}

                    {applicantCif.length > 0 && (
                        <div style={{ marginTop: '10px' }}>
                            <h6 style={{ fontWeight: '700', color: '#1D3557', marginBottom: '8px' }}>
                                Applicant CIF Details ({applicantCif.length}):
                            </h6>
                            <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
                                <table className="income-table" style={{ width: '100%', fontSize: '12px', background: '#fff' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ padding: '6px 8px' }}>CIF ID</th>
                                            <th style={{ padding: '6px 8px' }}>Name</th>
                                            <th style={{ padding: '6px 8px' }}>Contact</th>
                                            <th style={{ padding: '6px 8px' }}>Identity</th>
                                            <th style={{ padding: '6px 8px' }}>App ID & Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {applicantCif.map((cif, idx) => {
                                            const fullName = [cif.first_name, cif.middle_name, cif.last_name].filter(Boolean).join(' ') || 'N/A';
                                            const appId = cif.application?.id || 'N/A';
                                            const appStatus = cif.application?.application_status || 'N/A';
                                            return (
                                                <tr key={idx}>
                                                    <td style={{ padding: '6px 8px', fontWeight: 'bold' }}>
                                                        {cif.applicant_cif_Id || cif.dedupe_id || idx + 1}
                                                    </td>
                                                    <td style={{ padding: '6px 8px' }}>
                                                        <div><strong>{fullName}</strong></div>
                                                        {cif.dob && <div style={{ fontSize: '11px', color: '#6c757d' }}>DOB: {cif.dob} ({cif.age} yrs)</div>}
                                                    </td>
                                                    <td style={{ padding: '6px 8px' }}>
                                                        <div>{cif.mobile_no || 'N/A'}</div>
                                                        {cif.email_id && <div style={{ fontSize: '11px', color: '#6c757d' }}>{cif.email_id}</div>}
                                                    </td>
                                                    <td style={{ padding: '6px 8px' }}>
                                                        <div>PAN: {cif.applicant_panNo || 'N/A'}</div>
                                                        {cif.applicant_aadharNo && <div style={{ fontSize: '11px', color: '#6c757d' }}>Aadhar: {cif.applicant_aadharNo}</div>}
                                                    </td>
                                                    <td style={{ padding: '6px 8px' }}>
                                                        <div>App #{appId}</div>
                                                        <span style={{
                                                            display: 'inline-block',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            fontSize: '10px',
                                                            fontWeight: 'bold',
                                                            color: '#fff',
                                                            backgroundColor: appStatus === 'processing' ? '#17a2b8' : appStatus === 'completed' || appStatus === 'success' ? '#28a745' : '#6c757d',
                                                            marginTop: '2px'
                                                        }}>
                                                            {appStatus.toUpperCase()}
                                                        </span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* Asset Section */}
                <div style={{
                    marginBottom: '15px',
                    padding: '12px',
                    background: hasAssetErrors ? '#fff8f8' : '#f8f9fa',
                    borderRadius: '6px',
                    border: hasAssetErrors ? '1px solid #f5c6cb' : '1px solid #e9ecef'
                }}>
                    <h5 style={{ marginTop: 0, color: hasAssetErrors ? '#721c24' : '#1D3557', fontWeight: '700', borderBottom: '1px solid #dee2e6', paddingBottom: '6px' }}>
                        🚗 Asset Status
                    </h5>
                    <div style={{ fontSize: '13px', lineHeight: '1.6' }}>
                        <div><strong>Message:</strong> <span style={{ color: hasAssetErrors ? '#dc3545' : '#2b2d42' }}>{assetMessage}</span></div>
                        {assetId !== 'N/A' && <div><strong>Asset ID:</strong> {assetId}</div>}
                    </div>
                    {hasAssetErrors && renderErrorTable(parsedAssetErrors, "Asset Validation Errors")}
                </div>

                {/* Documents Status Section */}
                <div style={{
                    marginBottom: '10px',
                    padding: '12px',
                    background: isDocFailed ? '#fff8f8' : '#f8f9fa',
                    borderRadius: '6px',
                    border: isDocFailed ? '1px solid #f5c6cb' : '1px solid #e9ecef'
                }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #dee2e6', paddingBottom: '6px', marginBottom: '10px' }}>
                        <h5 style={{ margin: 0, color: isDocFailed ? '#721c24' : '#1D3557', fontWeight: '700' }}>
                            📄 Document Statuses
                        </h5>
                        <div style={{ fontSize: '12px', fontWeight: '600' }}>
                            <span className="badge badge-info" style={{ backgroundColor: '#17a2b8', color: '#fff', padding: '4px 8px', borderRadius: '4px', marginRight: '5px' }}>Total: {totalDocs}</span>
                            <span className="badge badge-success" style={{ backgroundColor: '#28a745', color: '#fff', padding: '4px 8px', borderRadius: '4px', marginRight: '5px' }}>Success: {successCount}</span>
                            <span className="badge badge-danger" style={{ backgroundColor: '#dc3545', color: '#fff', padding: '4px 8px', borderRadius: '4px' }}>Failed: {failureCount}</span>
                        </div>
                    </div>

                    {/* Document-level validation errors if any */}
                    {hasDocErrors && renderErrorTable(parsedDocErrors, "Document Validation Errors")}

                    {docResults.length > 0 ? (
                        <div style={{
                            maxHeight: '220px',
                            overflowY: 'auto',
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            marginTop: hasDocErrors ? '10px' : '0'
                        }}>
                            <table className="income-table" style={{ width: '100%', minWidth: '520px', fontSize: '12px', background: '#fff', margin: 0 }}>
                                <thead>
                                    <tr style={{ position: 'sticky', top: 0, zIndex: 1, background: '#f8f9fa' }}>
                                        <th style={{ width: '15%', padding: '6px 8px' }}>#</th>
                                        <th style={{ width: '22%', padding: '6px 8px' }}>Status</th>
                                        <th style={{ padding: '6px 8px' }}>Status Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {docResults.map((item, idx) => {
                                        const isSuccess = item.status === 'success' || item.status === true;
                                        let statusMsg = item.message || (isSuccess ? 'Uploaded successfully' : 'Failed');
                                        let docId = '';
                                        let itemErrors = item.errors;

                                        if (item.data) {
                                            const parsedData = parseJsonIfNeeded(item.data);
                                            if (parsedData?.data?.document_id) {
                                                docId = ` (Doc ID: ${parsedData.data.document_id})`;
                                            }
                                            if (parsedData?.errors) {
                                                itemErrors = itemErrors || parsedData.errors;
                                            }
                                        }

                                        if (item.step3Response?.responseBody) {
                                            const parsedErr = parseJsonIfNeeded(item.step3Response.responseBody);
                                            if (parsedErr?.message) {
                                                statusMsg = parsedErr.message;
                                            }
                                            if (parsedErr?.errors) {
                                                itemErrors = itemErrors || parsedErr.errors;
                                            }
                                        }

                                        return (
                                            <tr key={idx}>
                                                <td style={{ padding: '6px 8px', fontWeight: 'bold' }}>Doc {idx + 1}</td>
                                                <td style={{ padding: '6px 8px' }}>
                                                    <span style={{
                                                        display: 'inline-block',
                                                        padding: '2px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '11px',
                                                        fontWeight: 'bold',
                                                        color: '#fff',
                                                        backgroundColor: isSuccess ? '#28a745' : '#dc3545'
                                                    }}>
                                                        {isSuccess ? 'SUCCESS' : 'ERROR'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '6px 8px', wordBreak: 'break-word' }}>
                                                    <div>{statusMsg}{docId}</div>
                                                    {itemErrors && (
                                                        <div style={{ marginTop: '4px' }}>
                                                            {renderErrorTable(itemErrors, `Doc ${idx + 1} Validation Errors`)}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p style={{ fontSize: '13px', margin: 0, color: '#6c757d' }}>
                            {hasAppErrors && !docInfo ? 'Not initiated (Application step failed).' : 'No document results available.'}
                        </p>
                    )}
                </div>
            </div>
        );
    };

    const renderFallback = () => {
        if (!flowData) return <p className="text-center">No data available.</p>;
        if (typeof flowData === 'string') {
            return (
                <div style={{ padding: '10px', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                    <p>{flowData}</p>
                </div>
            );
        }
        return (
            <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                <pre style={{ fontSize: '12px', textAlign: 'left', background: '#f4f4f4', padding: '10px' }}>
                    {JSON.stringify(flowData, null, 2)}
                </pre>
            </div>
        );
    };

    return (
        <div className="background-blur income">
            <div className="popup-main small-popup container-fluid dbl-border" style={{ maxWidth: '750px', width: '92%' }}>
                <button className="close" onClick={() => closePopup()}>
                    &times;
                </button>
                <div className="col-xs-12" style={{ marginBottom: '15px' }}>
                    <h3 className="text-center blue-text" style={{ marginBottom: '20px', fontWeight: 'bold' }}>
                        Singularity Flow Details
                    </h3>
                    {hasSingularityFlow ? renderSingularityFlow() : renderFallback()}
                </div>
                
            </div>
        </div>
    );
};

export default SingularPopup;
